import eyeOffIcon from "@tabler/icons/outline/eye-off.svg";
import eyeIcon from "@tabler/icons/outline/eye.svg";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";
import { defineMessages, useIntl } from "react-intl";

import { cn } from "@/utils/cn";

import Icon from "../icon/icon";
import SvgIcon from "../icon/svg-icon";
import Tooltip from "../tooltip/tooltip";

const messages = defineMessages({
  showPassword: {
    id: "input.password.show_password",
    defaultMessage: "Show password",
  },
  hidePassword: {
    id: "input.password.hide_password",
    defaultMessage: "Hide password",
  },
});

const inputVariants = cva(
  "block w-full border text-base text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary disabled:text-muted-foreground disabled:opacity-50 sm:text-sm",
  {
    variants: {
      variant: {
        default: "rounded-md border-input bg-background",
        search: "rounded-full border-muted bg-muted focus:bg-background",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type InputVariants = VariantProps<typeof inputVariants>;
type InputVariant = NonNullable<InputVariants["variant"]>;

interface InputProps
  extends
    Pick<
      React.InputHTMLAttributes<HTMLInputElement>,
      | "maxLength"
      | "onChange"
      | "onBlur"
      | "type"
      | "autoComplete"
      | "autoCorrect"
      | "autoCapitalize"
      | "required"
      | "disabled"
      | "onClick"
      | "readOnly"
      | "min"
      | "pattern"
      | "onKeyDown"
      | "onKeyUp"
      | "onFocus"
      | "style"
      | "id"
    >,
    InputVariants {
  /** Put the cursor into the input on mount. */
  autoFocus?: boolean;
  /** The initial text in the input. */
  defaultValue?: string;
  /** Extra class names for the <input> element. */
  className?: string;
  /** Extra class names for the outer <div> element. */
  outerClassName?: string;
  /** URL to the svg icon. Cannot be used with prepend. */
  icon?: string;
  /** Internal input name. */
  name?: string;
  /** Text to display before a value is entered. */
  placeholder?: string;
  /** Text in the input. */
  value?: string | number;
  /** Change event handler for the input. */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** An element to display as prefix to input. Cannot be used with icon. */
  prepend?: React.ReactElement;
  /** An element to display as suffix to input. Cannot be used with password type. */
  append?: React.ReactElement;
  ref?: React.Ref<HTMLInputElement>;
}

/** Form input element. */
const Input = (props: InputProps) => {
  const intl = useIntl();

  const {
    type = "text",
    icon,
    className,
    outerClassName,
    append,
    prepend,
    variant,
    ref,
    ...filteredProps
  } = props;

  const [revealed, setRevealed] = React.useState(false);

  const isPassword = type === "password";

  // Password fields only reveal the show/hide toggle once they have a value.
  // Controlled inputs read that from `value`; uncontrolled ones track their
  // own input events since there's no `value` to observe.
  const isControlled = props.value !== undefined;
  const [hasTypedContent, setHasTypedContent] = React.useState(
    () => String(props.defaultValue ?? "").length > 0,
  );
  const hasContent = isControlled
    ? String(props.value ?? "").length > 0
    : hasTypedContent;
  const showReveal = isPassword && hasContent;

  const togglePassword = React.useCallback(() => {
    setRevealed((prev) => !prev);
  }, []);

  return (
    <div
      className={cn(
        "relative",
        variant === "search" ? "rounded-full" : "rounded-md",
        outerClassName,
      )}
    >
      {icon ? (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Icon
            src={icon}
            className="size-4 text-muted-foreground"
            aria-hidden="true"
          />
        </div>
      ) : null}

      {prepend ? (
        <div className="absolute inset-y-0 left-0 flex items-center">
          {prepend}
        </div>
      ) : null}

      <input
        data-slot="input"
        {...filteredProps}
        type={revealed ? "text" : type}
        ref={ref}
        onInput={
          isPassword && !isControlled
            ? (event) =>
                setHasTypedContent(event.currentTarget.value.length > 0)
            : undefined
        }
        className={cn(
          inputVariants({ variant }),
          {
            "pr-9 rtl:pr-3 rtl:pl-9": showReveal,
            "pr-7 rtl:pr-3 rtl:pl-7": !showReveal && (isPassword || append),
            "pl-8": typeof icon !== "undefined",
            "pl-16": typeof prepend !== "undefined",
          },
          className,
        )}
      />

      {append ? (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 rtl:right-auto rtl:left-0">
          {append}
        </div>
      ) : null}

      {showReveal ? (
        <Tooltip
          text={
            revealed
              ? intl.formatMessage(messages.hidePassword)
              : intl.formatMessage(messages.showPassword)
          }
        >
          <div className="absolute inset-y-0 right-0 flex items-center rtl:right-auto rtl:left-0">
            <button
              type="button"
              onClick={togglePassword}
              tabIndex={-1}
              className="h-full pr-3 pl-2 text-muted-foreground hover:text-foreground focus:ring-2 focus:ring-ring"
            >
              <SvgIcon
                src={revealed ? eyeOffIcon : eyeIcon}
                className="size-4"
              />
            </button>
          </div>
        </Tooltip>
      ) : null}
    </div>
  );
};

export { Input as default, Input, inputVariants, type InputVariant };
